import {
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Typography,
} from '@material-ui/core';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import { Column, Columns, Inline } from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { Fragment, ReactElement, useMemo, useRef, useState } from 'react';
import { formatPlural } from 'shared/helpers/IntlHelpers';
import {
  createSearchCriteria,
  getActiveSearchCount,
} from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { useScrollToTop } from 'shared/routing/ScrollToTop';
import styled from 'styled-components';
import { Heading } from '../../core/Heading';
import { getDriverSuggestedLoadOpenSource } from '../../core/DriverSuggestedLoadAnalytics';
import { trackLoadboardEvent } from '../../core/LoadboardAnalytics';
import { PostingListPagination } from '../../core/postings/PostingListPagination';
import { usePostingsPageContext } from '../../core/postings/PostingsPageContext';
import { PostingsPageState } from '../../core/postings/PostingsPageState';
import { isEmptyCriteria } from '../../data/dto/PostingsPageParamsDTO';
import { useLoadboardContext } from '../../data/LoadboardContext';
import { usePrivateLoadboardCount } from '../../data/LoadboardPostingAPI';
import { PostingListCard } from './PostingListCard';
import { PostingListEmpty } from './PostingListEmpty';
import { PrivatePostingsFindMore } from './PrivatePostingsFindMore';

const Wrapper = styled.div`
  flex: 1 1;
  display: flex;
  flex-direction: column;
  scroll-behavior: smooth;
`;

const Content = styled.div`
  flex: 1;
`;

export function PrivatePostingList({
  postings,
  postingsPageError,
  postingsPagination,
  isPostingsPageLoading,
}: PostingsPageState): ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const { data: privateLoadboard } = usePrivateLoadboardCount();
  const { setParams, params } = usePostingsPageContext();
  const {
    router: { openSearchPostingsScreen },
  } = useLoadboardContext();

  const isLastPage =
    !!postingsPagination &&
    params.current_page + 1 >=
      Math.ceil(postingsPagination.count / params.current_page_size);

  const activeSearchCount = useMemo(
    () => getActiveSearchCount(params.search_criteria),
    [params],
  );

  const suggestedLoadOpenSource = useMemo(
    () => getDriverSuggestedLoadOpenSource(params),
    [params],
  );

  const [isShippersExpended, setShippersExpended] = useState(false);
  const slicedShippers = useMemo(
    () =>
      privateLoadboard?.shippers.slice(0, isShippersExpended ? Infinity : 3),
    [privateLoadboard?.shippers, isShippersExpended],
  );

  useScrollToTop(
    ref.current,
    [params.current_tab, JSON.stringify(params.search_criteria)],
    { skipInitialMount: true },
  );

  return (
    <Wrapper ref={ref}>
      {postingsPageError ? (
        <Heading>Oops! Something Went wrong.</Heading>
      ) : !postings || isPostingsPageLoading ? (
        <Heading>Loading…</Heading>
      ) : (
        <>
          {postingsPagination && !isEmptyCriteria(params) && (
            <Box margin="xsmall" marginTop="xxsmall" marginBottom="xxsmall">
              <Columns>
                <Column>
                  {postingsPagination.count > 0 && (
                    <Typography>
                      {postingsPagination.count}{' '}
                      {formatPlural(
                        postingsPagination.count,
                        'Load Found',
                        'Loads Found',
                      )}
                    </Typography>
                  )}
                </Column>

                {activeSearchCount > 0 && (
                  <Column width="content">
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        setParams((prev) => ({
                          ...prev,
                          current_saved_search_guid: undefined,
                          search_criteria: createSearchCriteria({
                            shipper: prev.search_criteria.shipper,
                          }),
                        }));
                        trackLoadboardEvent({
                          name: 'SLB: UI Interaction',
                          type: 'Filter: Clicked Clear',
                        });
                      }}
                    >
                      Clear · {activeSearchCount}
                    </Button>
                  </Column>
                )}
              </Columns>
            </Box>
          )}

          {!params.search_criteria.shipper.include?.length && (
            <Box padding="xsmall">
              <List disablePadding={true} component={Paper}>
                {slicedShippers?.map((shipper, index) => {
                  return (
                    <Fragment key={shipper.guid}>
                      {index !== 0 && <Divider />}

                      <ListItem
                        button={true}
                        selected={
                          params.current_tab === 'private-loadboard' &&
                          params.search_criteria.shipper.include?.some(
                            (item) => item.guid === shipper.guid,
                          )
                        }
                        onClick={() => {
                          openSearchPostingsScreen({
                            tab: 'private-loadboard',
                            searchCriteria: {
                              shipper: {
                                include: [
                                  {
                                    guid: shipper.guid,
                                    display_name: shipper.name,
                                  },
                                ],
                              },
                            },
                          });
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            variant="circular"
                            alt={shipper.name}
                            src={shipper.logo_url}
                          />
                        </ListItemAvatar>

                        <ListItemText
                          primary={shipper.name}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />

                        <ListItemSecondaryAction>
                          {shipper.loads_count}
                        </ListItemSecondaryAction>
                      </ListItem>
                    </Fragment>
                  );
                })}

                {privateLoadboard && privateLoadboard.shippers.length > 3 && (
                  <>
                    <Divider />

                    <ListItem
                      button={true}
                      onClick={() => {
                        setShippersExpended((prev) => !prev);
                      }}
                    >
                      <ListItemText
                        primaryTypographyProps={{ variant: 'body2' }}
                        primary={
                          isShippersExpended ? (
                            <Inline verticalAlign="center">
                              <KeyboardArrowUp
                                fontSize="small"
                                color="action"
                              />
                              Hide
                            </Inline>
                          ) : (
                            <Inline verticalAlign="center">
                              <KeyboardArrowDown
                                fontSize="small"
                                color="action"
                              />
                              Show {privateLoadboard.shippers.length - 3} more
                            </Inline>
                          )
                        }
                      />
                    </ListItem>
                  </>
                )}
              </List>
            </Box>
          )}

          {postings.length === 0 ? (
            <PostingListEmpty />
          ) : (
            <>
              <Content>
                {postings.map((item) => (
                  <Box key={item.load.guid} margin="xsmall">
                    <PostingListCard
                      posting={item}
                      suggestedLoadOpenSource={suggestedLoadOpenSource}
                    />
                  </Box>
                ))}
              </Content>

              {isLastPage && (
                <Box margin="xsmall" marginTop="small" marginBottom="large">
                  <PrivatePostingsFindMore />
                </Box>
              )}
            </>
          )}
        </>
      )}

      <PostingListPagination pagination={postingsPagination} />
    </Wrapper>
  );
}
